// Pitstop Analysis Component
import React, { useState, useMemo } from 'react';
import { useF1Championship } from '../hooks/useF1Championship';
import { BarChart, LineChart, TableChart } from './charts';
import HeaderModule from './modules/HeaderModule';

const PitstopAnalysis = () => {
  const [selectedYear, setSelectedYear] = useState('all');
  
  // Load pitstop data
  const { data: pitstopsData, loading, error } = useF1Championship(['pit_stops.csv', 'races.csv']);
  const pitstops = pitstopsData?.['pit_stops'] || [];
  const races = pitstopsData?.['races'] || [];

  // Process pitstop data for visualization
  const pitstopData = useMemo(() => {
    if (!pitstopsData) return { averageByYear: [], totalByYear: [] };

    // Create race lookup for year mapping
    const raceYearMap = {};
    races.forEach(race => {
      raceYearMap[race.raceId] = parseInt(race.year);
    });

    // Calculate average pitstop time by year
    const pitstopsByYear = {};
    pitstops.forEach(pitstop => {
      const year = raceYearMap[pitstop.raceId];
      if (!year) return;
      
      if (!pitstopsByYear[year]) {
        pitstopsByYear[year] = { totalTime: 0, count: 0, times: [] };
      }
      
      const duration = parseFloat(pitstop.duration) || 0;
      pitstopsByYear[year].totalTime += duration;
      pitstopsByYear[year].count++;
      pitstopsByYear[year].times.push(duration);
    });

    // Calculate averages and totals
    const averageByYear = Object.entries(pitstopsByYear)
      .map(([year, data]) => ({
        label: year,
        value: parseFloat((data.totalTime / data.count).toFixed(2)),
        count: data.count,
        minTime: Math.min(...data.times).toFixed(2),
        maxTime: Math.max(...data.times).toFixed(2)
      }))
      .sort((a, b) => a.label - b.label);

    const totalByYear = Object.entries(pitstopsByYear)
      .map(([year, data]) => ({
        label: year,
        value: data.count
      }))
      .sort((a, b) => a.label - b.label);

    return {
      averageByYear,
      totalByYear,
      overallAverage: averageByYear.length > 0 
        ? parseFloat((averageByYear.reduce((sum, item) => sum + item.value, 0) / averageByYear.length).toFixed(2))
        : 0
    };
  }, [pitstops, races]);

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="theme-primary text-xl mb-4">Error loading pitstop data</div>
        <p className="theme-secondary mb-4">{error}</p>
      </div>
    );
  }

  const filteredAverageData = selectedYear === 'all' 
    ? pitstopData.averageByYear 
    : pitstopData.averageByYear.filter(item => item.label === parseInt(selectedYear));

  const filteredTotalData = selectedYear === 'all' 
    ? pitstopData.totalByYear 
    : pitstopData.totalByYear.filter(item => item.label === parseInt(selectedYear));

  return (
    <div className="pitstop-analysis space-y-6">
      {/* Header */}
      <HeaderModule data={{
        title: "F1 Pitstop Analysis",
        subtitle: "Average pitstop times and trends across F1 seasons",
        summary: [
          `${pitstops?.length || 0} pitstops`,
          `${pitstopData.overallAverage}s average time`
        ]
      }} />
      
      {/* Year Filter */}
      <div className="card">
        <h3 className="text-lg font-semibold theme-primary mb-4">Filter by Year</h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedYear('all')}
            className={`px-4 py-2 rounded-md border-2 transition-all ${
              selectedYear === 'all'
                ? 'theme-border theme-bg-secondary theme-accent'
                : 'theme-border-secondary hover:theme-border'
            }`}
          >
            All Years
          </button>
          {[...new Set(pitstopData.averageByYear.map(item => item.label))]
            .sort((a, b) => b - a)
            .slice(0, 10)
            .map(year => (
              <button
                key={year}
                onClick={() => setSelectedYear(year.toString())}
                className={`px-4 py-2 rounded-md border-2 transition-all ${
                  selectedYear === year.toString()
                    ? 'theme-border theme-bg-secondary theme-accent'
                    : 'theme-border-secondary hover:theme-border'
                }`}
              >
                {year}
              </button>
            ))}
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-80">
            <LineChart 
              data={filteredAverageData}
              title="Average Pitstop Time by Year"
              subtitle="Average duration in seconds"
              showLabels={true}
              animate={true}
            />
        </div>

        {/* Total Pitstops by Year */}
        <div className="h-80">
            <BarChart 
              data={filteredTotalData}
              title="Total Pitstops by Year"
              subtitle="Number of pitstops recorded"
              showLabels={true}
              animate={true}
            />
          </div>

        {/* Total Pitstops by Year
        <div className="h-80">
            <TableChart 
              data={filteredTotalData}
              title="Total Pitstops by Year"
              subtitle="Number of pitstops recorded"
              config={{
                columns: [
                  { key: 'label', label: 'Year' },
                  { key: 'value', label: 'Total Pitstops' }
                ],
                sortable: true
              }}
            />
          </div> */}
        </div>
      <div className="card">
        <h3 className="text-xl font-bold theme-primary mb-4">Pitstop Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 theme-bg-secondary rounded-lg">
            <div className="text-3xl font-bold theme-primary">
              {pitstopData.overallAverage}s
            </div>
            <div className="text-sm theme-secondary mt-1">Overall Average Time</div>
          </div>
          <div className="text-center p-4 theme-bg-secondary rounded-lg">
            <div className="text-3xl font-bold theme-primary">
              {filteredAverageData.length > 0 ? Math.min(...filteredAverageData.map(item => item.value)) : 0}s
            </div>
            <div className="text-sm theme-secondary mt-1">Fastest Average</div>
          </div>
          <div className="text-center p-4 theme-bg-secondary rounded-lg">
            <div className="text-3xl font-bold theme-primary">
              {filteredAverageData.length > 0 ? Math.max(...filteredAverageData.map(item => item.value)) : 0}s
            </div>
            <div className="text-sm theme-secondary mt-1">Slowest Average</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PitstopAnalysis;
